/**
 * Fängt das `beforeinstallprompt`-Event früh ab (schon beim App-Start, nicht erst wenn eine
 * Komponente gemountet ist – sonst geht das Event verloren) und stellt den nativen
 * Installationsdialog bereit.
 */
import { createSignal } from 'solid-js';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const [installEvent, setInstallEvent] = createSignal<BeforeInstallPromptEvent | null>(null);

/** Der native Installationsdialog ist verfügbar (Chrome/Edge/Android). */
export const canInstallNatively = () => installEvent() !== null;

/** Zeigt den nativen Dialog. Liefert das Ergebnis oder null, wenn er nicht verfügbar ist. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | null> {
  const ev = installEvent();
  if (!ev) return null;
  // Das Event ist nur einmal verwendbar.
  setInstallEvent(null);
  try {
    await ev.prompt();
    return (await ev.userChoice).outcome;
  } catch {
    return null;
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    setInstallEvent(e as BeforeInstallPromptEvent);
  });
  window.addEventListener('appinstalled', () => setInstallEvent(null));
}
